import {
  File,
  FileArchive,
  FileCode2,
  FileImage,
  FileSpreadsheet,
  FileText,
  Presentation,
  X,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { apiFetch } from '../../auth/apiClient'
import { useAttachmentDownload } from './useAttachmentDownload'
import styles from './AttachmentCards.module.scss'

export const MAX_ATTACHMENT_COUNT = 10
export const MAX_ATTACHMENT_BYTES = 30 * 1024 * 1024
export const MAX_CHAT_ATTACHMENT_TOTAL = 90 * 1024 * 1024
export const MAX_EMAIL_ATTACHMENT_TOTAL = 20 * 1024 * 1024
const ATTACHMENT_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.pdf', '.txt', '.md', '.csv', '.tsv',
  '.json', '.xml', '.yaml', '.yml', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.rtf', '.zip',
] as const
const ATTACHMENT_EXTENSION_SET = new Set<string>(ATTACHMENT_EXTENSIONS)
export const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(',')

export interface AttachmentMeta {
  id: string
  filename: string
  contentType: string
  size: number
}

export interface AttachmentCardItem {
  key: string
  filename: string
  contentType: string
  size: number
  file?: File
  url?: string
}

export function addAttachmentFiles(
  current: File[],
  incoming: File[],
  totalLimit: number,
): { files: File[]; error: string } {
  const additions = incoming.filter((file, index) =>
    !current.some((item) =>
      item.name === file.name && item.size === file.size && item.lastModified === file.lastModified)
    && incoming.findIndex((item) =>
      item.name === file.name && item.size === file.size && item.lastModified === file.lastModified) === index)
  if (current.length + additions.length > MAX_ATTACHMENT_COUNT)
    return { files: current, error: `Puoi allegare fino a ${MAX_ATTACHMENT_COUNT} file.` }
  for (const file of additions) {
    const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : ''
    if (!ATTACHMENT_EXTENSION_SET.has(extension))
      return { files: current, error: `Tipo di file non supportato: ${file.name}.` }
    if (file.size === 0) return { files: current, error: `${file.name} è vuoto.` }
    if (file.size > MAX_ATTACHMENT_BYTES)
      return { files: current, error: `${file.name} supera il limite di 30 MB.` }
  }
  if ([...current, ...additions].reduce((sum, item) => sum + item.size, 0) > totalLimit)
    return { files: current, error: `Gli allegati superano il limite di ${totalLimit / 1024 / 1024} MB.` }
  return { files: [...current, ...additions], error: '' }
}

function attachmentLabel(filename: string, size: number): string {
  const extension = filename.includes('.') ? filename.split('.').pop()!.toUpperCase() : ''
  const formatted = formatAttachmentSize(size)
  return extension && extension.length <= 4 ? `${extension} · ${formatted}` : formatted
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

function kindOf(filename: string, contentType: string) {
  const extension = filename.split('.').pop()?.toLowerCase() ?? ''
  if (contentType.startsWith('image/')) return 'image'
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || ['xls', 'xlsx', 'csv', 'tsv'].includes(extension)) return 'spreadsheet'
  if (contentType.includes('presentation') || ['ppt', 'pptx', 'key'].includes(extension)) return 'presentation'
  if (contentType.includes('pdf') || extension === 'pdf') return 'pdf'
  if (contentType.includes('word') || ['doc', 'docx', 'rtf', 'txt', 'md'].includes(extension)) return 'document'
  if (contentType.includes('zip') || ['zip', 'rar', '7z', 'gz'].includes(extension)) return 'archive'
  if (contentType.startsWith('text/') || ['json', 'xml', 'yaml', 'yml', 'js', 'ts', 'py', 'sql'].includes(extension)) return 'code'
  return 'file'
}

function AttachmentIcon({ kind }: { kind: ReturnType<typeof kindOf> }) {
  const props = { size: 19, strokeWidth: 1.8, 'aria-hidden': true } as const
  if (kind === 'image') return <FileImage {...props} />
  if (kind === 'spreadsheet') return <FileSpreadsheet {...props} />
  if (kind === 'presentation') return <Presentation {...props} />
  if (kind === 'pdf' || kind === 'document') return <FileText {...props} />
  if (kind === 'archive') return <FileArchive {...props} />
  if (kind === 'code') return <FileCode2 {...props} />
  return <File {...props} />
}

function CardContent({ item }: { item: AttachmentCardItem }) {
  const [preview, setPreview] = useState('')
  const image = item.contentType.startsWith('image/')

  useEffect(() => {
    if (!image) return
    let objectUrl = ''
    let cancelled = false
    void (async () => {
      const blob = item.file
        ? item.file
        : item.url
          ? await apiFetch(item.url).then((response) => response.ok ? response.blob() : null)
          : null
      if (!blob || cancelled) return
      objectUrl = URL.createObjectURL(blob)
      setPreview(objectUrl)
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [image, item.file, item.url])

  const kind = kindOf(item.filename, item.contentType)
  return (
    <>
      {image && preview ? (
        <img className={styles.thumbnail} src={preview} alt="" />
      ) : (
        <span className={styles.icon} data-kind={kind}>
          <AttachmentIcon kind={kind} />
        </span>
      )}
      <span className={styles.meta}>
        <strong title={item.filename}>{item.filename}</strong>
        <span>{attachmentLabel(item.filename, item.size)}</span>
      </span>
    </>
  )
}

export function AttachmentCards({
  items,
  onRemove,
}: {
  items: AttachmentCardItem[]
  onRemove?: (key: string) => void
}) {
  const { download, error, busy } = useAttachmentDownload()
  if (items.length === 0) return null
  return (
    <div className={styles.list} aria-label="Allegati">
      {items.map((item) => {
        const content = <CardContent item={item} />
        let body: ReactNode = content
        if (item.url) {
          body = <button
            type="button"
            className={styles.open}
            disabled={busy}
            onClick={() => void download(item.url!, item.filename)}
          >{content}</button>
        }
        return (
          <span className={styles.card} key={item.key}>
            {body}
            {onRemove ? (
              <button
                type="button"
                className={styles.remove}
                aria-label={`Rimuovi ${item.filename}`}
                onClick={() => onRemove(item.key)}
              >
                <X size={13} aria-hidden />
              </button>
            ) : null}
          </span>
        )
      })}
      {error && <span role="alert">{error}</span>}
    </div>
  )
}

export function filesAsCards(files: File[]): AttachmentCardItem[] {
  return files.map((file) => ({
    key: `${file.name}:${file.size}:${file.lastModified}`,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    file,
  }))
}
